import { NavLink, Outlet, Link, useNavigate, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { useTr } from "@/i18n/translator";
import {
  LayoutDashboard,
  Calendar,
  Sparkles,
  Image as ImageIcon,
  Archive,
  Settings,
  BarChart3,
  Radar,
  Ear,
  Plug,
  Inbox,
  LogOut,
  Gift,
  Zap,
  Shield,
  Video,
  Briefcase,
  ChevronDown,
  Check,
  Key,
  Lock,
  Store,
  Search,
  Rocket,
  Users,
  LifeBuoy,
  Wand2,
  Megaphone,
  FileText,
  Crown,
} from "lucide-react";
import { planIncludes } from "@/lib/planFeatures";

const NAV = [
  { to: "/dashboard", key: "overview", icon: LayoutDashboard, end: true, testid: "overview", feature: null },
  { to: "/dashboard/start", key: "start", icon: Rocket, testid: "start", feature: null },
  { to: "/dashboard/calendar", key: "calendar", icon: Calendar, testid: "calendar", feature: null },
  { to: "/dashboard/generator", key: "generator", icon: Sparkles, testid: "generator", feature: null },
  { to: "/dashboard/marketplace", key: "marketplace", icon: Store, testid: "marketplace", feature: "marketplace" },
  { to: "/dashboard/seo", key: "seo", icon: Search, testid: "seo", feature: "seo" },
  { to: "/dashboard/images", key: "images", icon: ImageIcon, testid: "images", feature: "images" },
  { to: "/dashboard/videos", key: "videos", icon: Video, testid: "videos", feature: "videos" },
  { to: "/dashboard/campaigns", key: "campaigns", icon: Megaphone, testid: "campaigns", feature: null },
  { to: "/dashboard/feed", key: "feed", icon: Inbox, testid: "feed", feature: null },
  { to: "/dashboard/bank", key: "bank", icon: Archive, testid: "bank", feature: null },
  { to: "/dashboard/analytics", key: "analytics", icon: BarChart3, testid: "analytics", feature: null },
  { to: "/dashboard/competitors", key: "competitors", icon: Radar, testid: "competitors", feature: "competitors" },
  { to: "/dashboard/listening", key: "listening", icon: Ear, testid: "listening", feature: "listening" },
  { to: "/dashboard/accounts", key: "accounts", icon: Plug, testid: "accounts", feature: null },
  { to: "/dashboard/referrals", key: "referrals", icon: Gift, testid: "referrals", feature: null },
  { to: "/dashboard/support", key: "user_support", icon: LifeBuoy, testid: "user-support", feature: null },
  { to: "/dashboard/subscription", key: "subscription", icon: Zap, testid: "subscription", feature: null },
  { to: "/dashboard/invoices", key: "invoices", icon: FileText, testid: "invoices", feature: null },
  { to: "/dashboard/brands", key: "brands_overview", icon: Briefcase, testid: "brands-overview", feature: null },
  { to: "/dashboard/brand", key: "brand", icon: Settings, testid: "brand", feature: null },
  { to: "/dashboard/brand-builder", key: "brand_builder", icon: Wand2, testid: "brand-builder", feature: null },
  { to: "/dashboard/security", key: "security", icon: Lock, testid: "security", feature: null },
  { to: "/dashboard/api-keys", key: "api_keys", icon: Key, testid: "api-keys", feature: "apiPublic" },
];

const ADMIN_NAV = [
  { to: "/dashboard/admin", key: "admin_mrr", icon: Shield, testid: "admin" },
  { to: "/dashboard/admin/users", key: "admin_users", icon: Users, testid: "admin-users" },
  { to: "/dashboard/admin/support", key: "admin_support", icon: LifeBuoy, testid: "admin-support" },
  { to: "/dashboard/admin/code-changes", key: "admin_code_changes", icon: Wand2, testid: "admin-code-changes" },
];

const SUPER_ADMIN_NAV = [
  { to: "/dashboard/super-admin", key: "super_admin", icon: Crown, testid: "super-admin" },
];

export default function DashboardLayout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const { t } = useTranslation();
  const tr = useTr();
  const isAdmin = user?.role === "admin" || user?.role === "super_admin";
  const isSuperAdmin = user?.role === "super_admin";
  const items = isSuperAdmin
    ? [...NAV, ...ADMIN_NAV, ...SUPER_ADMIN_NAV]
    : isAdmin ? [...NAV, ...ADMIN_NAV] : NAV;
  const [brands, setBrands] = useState([]);
  const [switching, setSwitching] = useState(false);
  const [openSwitcher, setOpenSwitcher] = useState(false);

  const loadBrands = async () => {
    try {
      const { data } = await api.get("/brands");
      setBrands(data);
    } catch {}
  };

  useEffect(() => { loadBrands(); }, []);

  const active = items.find((n) => (n.end ? loc.pathname === n.to : loc.pathname.startsWith(n.to)));
  const activeLabel = active ? t(`dashboard_nav.${active.key}`) : "Dashboard";
  const activeBrand = brands.find((b) => b.is_active);
  const primary = activeBrand?.primary_color || "#E53935";

  const switchBrand = async (brandId) => {
    setSwitching(true);
    try {
      await api.post("/brands/activate", { brand_id: brandId });
      toast.success("Marca activa actualizada");
      setOpenSwitcher(false);
      // Forzamos recarga completa: todos los módulos (calendar, analytics, feed,
      // competitors, accounts, bank, images, videos) refetchean datos de la nueva marca.
      window.location.reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al cambiar marca");
    } finally {
      setSwitching(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F4F4F0] text-[#0A0A0A] flex">
      {/* Sidebar */}
      <aside className="w-64 shrink-0 bg-white border-r border-[#0A0A0A] sticky top-0 h-screen flex flex-col" data-testid="dashboard-sidebar">
        <div className="p-5 border-b border-[#0A0A0A] flex items-center gap-3">
          <img src="/logo-cia.png" alt="CIA Marketing" className="w-10 h-10 object-contain" />
          <div className="flex flex-col leading-none">
            <span className="font-display text-base">CIA Marketing<span className="text-[#E53935]">®</span></span>
            <span className="font-mono-mark text-[9px] text-[#5C5C5C]">{tr("AUTOMATIZACIÓN IA")}</span>
          </div>
        </div>
        <nav className="flex-1 overflow-y-auto py-2">
          {NAV.map((n) => {
            const locked = n.feature && !planIncludes(user?.plan || "free", n.feature, user);
            return (
              <NavLink
                key={n.to}
                to={locked ? "/dashboard/subscription" : n.to}
                end={n.end}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-5 py-3 border-l-2 font-mono-mark text-[10px] ${
                    isActive && !locked
                      ? "border-[#E53935] bg-[#F4F4F0] text-[#0A0A0A]"
                      : "border-transparent text-[#5C5C5C] hover:text-[#0A0A0A] hover:bg-[#F4F4F0]"
                  } ${locked ? "opacity-50" : ""}`
                }
                data-testid={`nav-${n.testid}${locked ? "-locked" : ""}`}
                title={locked ? "Requiere plan superior" : undefined}
              >
                {n.icon && <n.icon className="w-4 h-4" />}
                <span className="flex-1 truncate">{t(`dashboard_nav.${n.key}`).toUpperCase()}</span>
                {locked && <Lock className="w-3 h-3 text-[#E53935]" />}
              </NavLink>
            );
          })}
          {isAdmin && (
            <>
              <div className="px-5 py-2 mt-3 border-t border-[#0A0A0A] font-mono-mark text-[9px] text-[#5C5C5C]">
                ADMIN
              </div>
              {ADMIN_NAV.map((n) => (
                <NavLink
                  key={n.to}
                  to={n.to}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-5 py-3 border-l-2 font-mono-mark text-[10px] ${
                      isActive
                        ? "border-[#E53935] bg-[#F4F4F0] text-[#0A0A0A]"
                        : "border-transparent text-[#5C5C5C] hover:text-[#0A0A0A] hover:bg-[#F4F4F0]"
                    }`
                  }
                  data-testid={`nav-${n.testid}`}
                >
                  <n.icon className="w-4 h-4" />
                  {t(`dashboard_nav.${n.key}`).toUpperCase()}
                </NavLink>
              ))}
              {isSuperAdmin && (
                <>
                  <div className="px-5 py-2 mt-2 font-mono-mark text-[9px] text-[#FFD600] bg-[#0A0A0A]">
                    ⚡ SUPER ADMIN
                  </div>
                  {SUPER_ADMIN_NAV.map((n) => (
                    <NavLink
                      key={n.to}
                      to={n.to}
                      className={({ isActive }) =>
                        `flex items-center gap-3 px-5 py-3 border-l-2 font-mono-mark text-[10px] ${
                          isActive
                            ? "border-[#FFD600] bg-[#0A0A0A] text-[#FFD600]"
                            : "border-transparent text-[#5C5C5C] hover:text-[#0A0A0A] hover:bg-[#F4F4F0]"
                        }`
                      }
                      data-testid={`nav-${n.testid}`}
                    >
                      <n.icon className="w-4 h-4" />
                      {(t(`dashboard_nav.${n.key}`) || "Super Admin").toUpperCase()}
                    </NavLink>
                  ))}
                </>
              )}
            </>
          )}
        </nav>
        <div className="border-t border-[#0A0A0A] p-5">
          <div className="font-mono-mark text-[10px] text-[#5C5C5C]">CLEARANCE</div>
          <div className="font-display text-sm mt-1 truncate">{user?.name || "—"}</div>
          <div className="font-mono-data text-xs text-[#5C5C5C] truncate">{user?.email}</div>
          <div className="mt-3 flex items-center justify-between">
            <span className="inline-block px-2 py-1 bg-[#0A0A0A] text-[#FFD600] font-mono-mark text-[9px] uppercase">
              {user?.plan || "FREE"}
            </span>
            <button
              onClick={() => {
                logout();
                nav("/");
              }}
              className="font-mono-mark text-[10px] text-[#5C5C5C] hover:text-[#E53935] flex items-center gap-1"
              data-testid="logout-button"
            >
              <LogOut className="w-3 h-3" /> {tr("SALIR")}
            </button>
          </div>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 min-w-0">
        <header className="sticky top-0 z-30 bg-[#F4F4F0] border-b border-[#0A0A0A] px-6 md:px-10 h-16 flex items-center justify-between" style={{ "--brand-primary": primary }}>
          <div className="flex items-center gap-3 min-w-0">
            <span className="font-mono-mark text-[10px] text-[#5C5C5C] shrink-0">DOSSIER</span>
            <span className="font-mono-mark text-[10px] text-[#0A0A0A] shrink-0">/</span>
            <span className="font-mono-mark text-[10px] truncate" style={{ color: primary }} data-testid="active-section">{activeLabel.toUpperCase()}</span>
          </div>

          <div className="flex items-center gap-3">
            <LanguageSwitcher />
            {/* Agency brand switcher */}
            {brands.length > 1 && (
              <div className="relative">
                <button
                  onClick={() => setOpenSwitcher(!openSwitcher)}
                  disabled={switching}
                  className="flex items-center gap-2 font-mono-mark text-[10px] bg-white border border-[#0A0A0A] px-3 py-2 hover:bg-[#F4F4F0] disabled:opacity-50"
                  data-testid="brand-switcher"
                >
                  <Briefcase className="w-3 h-3" />
                  <span className="truncate max-w-[160px]">{activeBrand?.brand_name || tr("MARCA")}</span>
                  <ChevronDown className={`w-3 h-3 transition-transform ${openSwitcher ? "rotate-180" : ""}`} />
                </button>
                {openSwitcher && (
                  <div className="absolute right-0 top-full mt-1 w-72 bg-white border border-[#0A0A0A] brutal-shadow z-50" data-testid="brand-switcher-menu">
                    <div className="px-3 py-2 border-b border-[#0A0A0A] font-mono-mark text-[9px] text-[#5C5C5C]">
                      {tr("MARCAS DEL DOSSIER")} · {brands.length}
                    </div>
                    <ul className="max-h-80 overflow-y-auto">
                      {brands.map((b) => (
                        <li key={b.id}>
                          <button
                            onClick={() => switchBrand(b.id)}
                            disabled={b.is_active || switching}
                            className={`w-full flex items-center justify-between px-3 py-2 text-left font-mono-data text-sm border-b border-[#0A0A0A]/10 ${b.is_active ? "bg-[#FFD600]" : "hover:bg-[#F4F4F0]"} disabled:cursor-default`}
                            data-testid={`brand-switch-${b.id}`}
                          >
                            <div className="flex items-center gap-2 min-w-0">
                              <span className="w-2 h-2 shrink-0 rounded-full" style={{ backgroundColor: b.primary_color || "#E53935" }} />
                              <span className="truncate">{b.brand_name}</span>
                            </div>
                            {b.is_active && <Check className="w-3 h-3 text-[#E53935]" />}
                          </button>
                        </li>
                      ))}
                    </ul>
                    <div className="px-3 py-2 border-t border-[#0A0A0A]">
                      <Link to="/dashboard/brand" onClick={() => setOpenSwitcher(false)} className="font-mono-mark text-[9px] text-[#E53935] underline">
                        {tr("GESTIONAR MARCAS")} →
                      </Link>
                    </div>
                  </div>
                )}
              </div>
            )}
            <Link to="/dashboard/generator" className="font-mono-mark text-[10px] text-white px-3 py-2 hover-brutal border" style={{ backgroundColor: primary, borderColor: primary }} data-testid="topbar-generate">
              {t("common.new_post").toUpperCase()}
            </Link>
          </div>
        </header>
        <div className="p-6 md:p-10">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
